import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { d, ORDER_STATUS } from "@/lib/api";
import { Loading, useFetch } from "./ui";

const url = (p) => `${window.location.origin}/administrativo${p}`;
const Head = ({ title, number, date, qr }) => (
  <div className="flex justify-between items-start border-b-2 border-black pb-4 mb-5">
    <div><img src="/brand/logo-sm.png" alt="E³ Impressão 3D" className="h-16" /><h1 className="text-2xl font-bold mt-2">{title}</h1><div className="text-sm">Nº <b className="font-mono">{number}</b> · {d(date)}</div></div>
    <QRCodeSVG value={qr} size={96} />
  </div>
);
const Bar = () => (
  <div className="no-print flex gap-2 mb-4"><button className="btn-primary btn-sm" onClick={() => window.print()} data-testid="do-print">Imprimir</button><button className="btn-ghost btn-sm" onClick={() => window.close()}>Fechar</button></div>
);
const opts = (i) => [...Object.entries(i.colors || {}), ...Object.entries(i.options || {})].filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(" · ");
const custom = (c) => Object.entries(c || {}).map(([k, v]) => `${k}: ${typeof v === "object" ? v.name : v}`).join(" · ");

function OrderPrint({ o }) {
  return (
    <div className="print-page bg-white text-black p-8 max-w-[210mm] mx-auto" data-testid="print-order">
      <Head title="Pedido" number={o.number} date={o.created_at} qr={url(`/pedidos/${o.id}`)} />
      <div className="grid grid-cols-2 gap-6 text-sm mb-6">
        <div><b>Cliente</b><div>{o.customer.name}</div><div>CPF {o.customer.cpf}</div><div>{o.customer.phone} · {o.customer.email}</div></div>
        <div><b>Entrega · {o.shipping_method?.name}</b><div>{o.address.street}, {o.address.number} {o.address.complement}</div><div>{o.address.district} — {o.address.city}/{o.address.state}</div><div>CEP {o.address.cep}</div><div>Prazo: até {o.shipping_method?.total_days} dias úteis</div></div>
      </div>
      <table className="w-full text-sm border-collapse"><thead><tr className="border-b border-black text-left"><th className="py-2">Produto</th><th>SKU</th><th>Qtd</th><th>Cor / Opções</th><th>Personalização</th></tr></thead>
        <tbody>{o.items.map((i, k) => <tr key={k} className="border-b"><td className="py-2">{i.name}</td><td className="font-mono text-xs">{i.sku}</td><td>{i.qty}</td><td className="text-xs">{opts(i)}</td><td className="text-xs">{custom(i.custom)}</td></tr>)}</tbody></table>
      <div className="mt-6 text-sm"><b>Observações:</b> {o.notes || "—"}</div>
      <div className="text-sm mt-1"><b>Status:</b> {ORDER_STATUS[o.status]}</div>
    </div>
  );
}

function OpPrint({ op }) {
  const o = op.order || {};
  const it = o.items?.[op.item_index] || {};
  return (
    <div className="print-page bg-white text-black p-8 max-w-[210mm] mx-auto" data-testid="print-op">
      <Head title="Ordem de Produção" number={op.number} date={op.created_at} qr={url(`/producao?op=${op.id}`)} />
      <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
        {[["Pedido", op.order_number], ["Cliente", op.customer], ["Produto", op.product], ["SKU", op.sku], ["Quantidade", op.qty], ["Material", op.material], ["Cores utilizadas", Object.values(op.colors || {}).filter(Boolean).join(", ")], ["Opções", Object.entries(op.options || {}).map(([k, v]) => `${k}: ${v}`).join(", ")],
          ["Arquivo/modelo", op.file_url || "—"], ["Personalização", custom(op.custom) || "—"], ["Prazo", `${op.deadline_days} dias úteis`], ["Prioridade", op.priority]].map(([k, v]) => <div key={k} className="border-b py-1.5"><span className="text-xs uppercase">{k}</span><div className="font-semibold">{v || "—"}</div></div>)}
      </div>
      <div className="mt-6 text-sm"><b>Observações técnicas:</b><div className="border min-h-20 p-2 mt-1">{op.notes}</div></div>
      <div className="mt-6 grid grid-cols-5 gap-2 text-sm">{["Produção iniciada", "Impressão concluída", "Acabamento", "Qualidade", "Embalagem"].map((s) => <div key={s} className="border p-2 flex items-center gap-2"><span className="h-4 w-4 border border-black inline-block" />{s}</div>)}</div>
      {it.unit_price === undefined ? null : <div className="mt-4 text-xs">Item do pedido #{op.item_index + 1}</div>}
    </div>
  );
}

function LabelPrint({ o }) {
  return (
    <div className="print-page bg-white text-black p-4 mx-auto space-y-3" style={{ width: "100mm" }} data-testid="print-label">
      {o.items.map((i, k) => (
        <div key={k} className="border-2 border-black p-3 flex gap-3" style={{ breakInside: "avoid" }}>
          <div className="flex-1 text-sm"><img src="/brand/logo-sm.png" alt="E³" className="h-8" /><div className="font-mono font-bold mt-1">{o.number}</div><div>{o.customer.name}</div><div className="font-semibold">{i.name}</div><div className="font-mono text-xs">SKU {i.sku} · Qtd {i.qty}</div></div>
          <QRCodeSVG value={url(`/pedidos/${o.id}`)} size={80} />
        </div>
      ))}
    </div>
  );
}

export default function Print() {
  const { kind, id } = useParams();
  const [data] = useFetch(kind === "op" ? `/admin/production/${id}` : `/admin/orders/${id}`);
  useEffect(() => { document.body.style.background = "#fff"; return () => { document.body.style.background = ""; }; }, []);
  if (!data) return <Loading />;
  return (
    <div className="min-h-screen bg-white p-4"><div className="max-w-[210mm] mx-auto"><Bar /></div>
      {kind === "op" ? <OpPrint op={data} /> : kind === "etiqueta" ? <LabelPrint o={data} /> : <OrderPrint o={data} />}</div>
  );
}
